// src/pages/MarketplaceIntegration/MarketplaceOrders.jsx

import React, { useState, useEffect } from 'react';
import useMarketplaceIntegration from './useMarketplaceIntegration';

const MarketplaceOrders = ({ marketplaces }) => {
  const { getMarketplaceOrders } = useMarketplaceIntegration();
  const [orders, setOrders] = useState({});
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch orders when a marketplace is selected
  useEffect(() => {
    const fetchOrders = async () => {
      if (!selectedMarketplace) {
        setOrders({});
        return;
      }

      setLoading(true);
      try {
        // Find the marketplace ID by name
        const marketplace = marketplaces.find(m => m.name.toLowerCase() === selectedMarketplace);
        if (!marketplace) {
          throw new Error('Marketplace not found');
        }

        // Fetch orders from the selected marketplace
        const marketplaceOrders = await getMarketplaceOrders(marketplace.id);
        
        setOrders(prev => ({
          ...prev,
          [selectedMarketplace]: marketplaceOrders
        }));
      } catch (error) {
        console.error('Error fetching marketplace orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [selectedMarketplace, marketplaces, getMarketplaceOrders]);

  const handleMarketplaceChange = (e) => {
    setSelectedMarketplace(e.target.value);
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'processing': return 'İşleniyor';
      case 'shipped': return 'Gönderildi';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal Edildi';
      case 'Sipariş Alındı': return 'Sipariş Alındı';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#F59E0B';
      case 'processing': return '#3B82F6';
      case 'shipped': return '#8B5CF6';
      case 'delivered': return '#10B981';
      case 'cancelled': return '#EF4444';
      case 'Sipariş Alındı': return '#10B981';
      default: return '#9CA3AF';
    }
  };

  const filteredOrders = selectedMarketplace 
    ? (orders[selectedMarketplace] || []).filter(order =>
        (order.customer && order.customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.orderNumber && order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  return (
    <div className="marketplace-orders">
      <div className="orders-header">
        <h2>Pazaryerinden Gelen Siparişler</h2>
        <p>Pazaryerlerinden gelen siparişleri görüntüleyin ve yönetin</p>
      </div>

      <div className="orders-controls">
        <div className="form-group">
          <label htmlFor="marketplace-select">Pazaryeri Seçin</label>
          <select
            id="marketplace-select"
            value={selectedMarketplace}
            onChange={handleMarketplaceChange}
          >
            <option value="">Pazaryeri Seçin</option>
            {marketplaces.map(marketplace => (
              <option key={marketplace.id} value={marketplace.name.toLowerCase()}>
                {marketplace.name}
              </option>
            ))}
          </select>
        </div>

        {selectedMarketplace && (
          <div className="form-group">
            <label htmlFor="search">Sipariş Ara</label>
            <input
              type="text"
              id="search"
              placeholder="Müşteri adı veya sipariş numarası ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <p>Pazaryeri siparişleri yükleniyor...</p>
        </div>
      ) : selectedMarketplace ? (
        <div className="orders-table-container">
          {filteredOrders.length === 0 ? (
            <div className="empty-state">
              <p>Seçilen pazaryerinden gelen sipariş bulunamadı</p>
              <p style={{ fontSize: '14px', marginTop: '10px' }}>
                Siparişleri görmek için önce pazaryeri bağlantısını yapıp senkronizasyon yapmalısınız.
              </p>
            </div>
          ) : (
            <>
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Sipariş Numarası</th>
                    <th>Müşteri</th>
                    <th>Tarih</th>
                    <th>Toplam Tutar</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td>{order.orderNumber}</td>
                      <td>{order.customer}</td>
                      <td>{order.date ? new Date(order.date).toLocaleDateString('tr-TR') : 'Tarih Yok'}</td>
                      <td>{typeof order.total === 'number' ? `${order.total.toFixed(2)} ₺` : 'Tutar Yok'}</td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {getStatusText(order.status)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="view-button">Detaylar</button>
                          <button className="import-button">İçe Aktar</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="orders-summary">
                <h3>Sipariş Özeti</h3>
                <div className="summary-content">
                  <div className="summary-item">
                    <span className="summary-label">Toplam Sipariş:</span>
                    <span className="summary-value">{filteredOrders.length}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <p>Başlamak için bir pazaryeri seçin</p>
        </div>
      )}
    </div>
  );
};

export default MarketplaceOrders;